import { Button, CircularProgress, useTheme } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { axiosInstance, LocalStorage } from '../../helpers'
import { Container } from '../Container'
import {
    Address,
    Company,
    IdentifiableNamedObject,
    Organization,
} from '../../models'
import { toast } from 'react-toastify'
import { Selector } from '../Selector'
import { useOnboardEntrataPropertiesToCompany } from '../../hooks/useOnboardEntrataPropertiesToCompany'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    onSuccess?: () => void
}

interface EntrataProperty extends Address, IdentifiableNamedObject {}

export const MultiEntrataSiteOnboardForm = (props: Props) => {
    const { onSuccess } = props
    const theme = useTheme()
    const {
        onboard,
        isLoading: isSubmitting,
    } = useOnboardEntrataPropertiesToCompany()
    const [socket, setSocket] = useState<WebSocket | null>(null)

    const [organizationList, setOrganizationList] = useState<
        Organization[] | null
    >(null)

    const [searchLoading, setSearchLoading] = useState(false)
    const [entrataDone, setEntrataDone] = useState(false)
    const [apartmentFetchDone, setApartmentFetchDone] = useState(false)
    const loadingDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [selectedOrgId, setSelectedOrgId] = useState(-1)

    const [entrataProperties, setEntrataProperties] = useState<
        EntrataProperty[]
    >([])
    const [ezosApartments, setEzosApartments] = useState<Company[]>([])

    const [selectedEzosAptId, setSelectedEzosAptId] = useState(-1)
    const [selectedEntrataIds, setSelectedEntrataIds] = useState<number[]>([])
    const [hasSearched, setHasSearched] = useState(false)

    const selectorStyle: React.CSSProperties = {
        flex: 1,
        marginRight: theme.spacing(1),
    }

    const buttonStyle: React.CSSProperties = { maxHeight: 50, minWidth: 125 }

    useEffect(() => {
        axiosInstance
            .get('company/organization/', { params: { entrata: true } })
            .then((res) => {
                setOrganizationList(res.data)
            })
            .catch((e) => toast.error(e))
    }, [])

    useEffect(() => {
        if (apartmentFetchDone && entrataDone) {
            setSearchLoading(false)
            setApartmentFetchDone(false)
            setEntrataDone(false)
        }
    }, [apartmentFetchDone, entrataDone])

    useEffect(() => {
        const wsBaseUrl = process.env.REACT_APP_WEBSOCKET_BASE_URL
        if (!wsBaseUrl) {
            toast.error('WebSocket configuration error')
            return
        }

        const token = localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
        const url = `${wsBaseUrl}integration/entrata-properties/?token=${token}`
        const ws = new WebSocket(url)

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data)

            if (Array.isArray(data)) {
                const newEntrataProperties: EntrataProperty[] = data.map(
                    (property: any) => ({
                        name: property['MarketingName'],
                        id: property['PropertyID'],
                        address1: property['Address']['Address'],
                        address2: '',
                        city: property['Address']['City'],
                        state: property['Address']['State'],
                        zip_code: property['Address']['PostalCode'],
                        country: property['Address']['Country'],
                        utc_offset: 0,
                        dst_offset: 0,
                        created_date: new Date().toISOString(),
                        modified_date: new Date().toISOString(),
                        timezone_id: '',
                        timezone_name: '',
                    }),
                )
                setEntrataProperties(newEntrataProperties)

                // Debounce: only signal done after 600ms of no further messages
                if (loadingDoneTimer.current) {
                    clearTimeout(loadingDoneTimer.current)
                }
                loadingDoneTimer.current = setTimeout(() => {
                    setEntrataDone(true)
                }, 600)
            } else if (data.type === 'error') {
                toast.error(data.message || 'Error fetching Entrata properties')
                if (loadingDoneTimer.current) {
                    clearTimeout(loadingDoneTimer.current)
                }
                setEntrataDone(true)
            }
        }

        ws.onerror = () => {
            toast.error('Error connecting to WebSocket')
            setEntrataDone(true)
        }

        setSocket(ws)

        return () => {
            ws.close()
            if (loadingDoneTimer.current) {
                clearTimeout(loadingDoneTimer.current)
            }
        }
    }, [])

    const onSearch = () => {
        const selectedOrg = organizationList?.find(
            (org) => org.id === selectedOrgId,
        )
        if (!selectedOrg) return

        setSearchLoading(true)
        setHasSearched(true)
        setEntrataDone(false)
        setApartmentFetchDone(false)
        setEntrataProperties([])
        setEzosApartments([])
        setSelectedEzosAptId(-1)
        setSelectedEntrataIds([])

        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(
                JSON.stringify({
                    type: 'get_entrata_properties',
                    org_id: selectedOrg.id,
                }),
            )
        } else {
            toast.error('WebSocket connection not available')
            setEntrataDone(true)
        }

        axiosInstance
            .get('company/apartment/', {
                params: {
                    organization_id: selectedOrg.id,
                },
            })
            .then((res) => setEzosApartments(res.data))
            .catch((e) => {
                toast.error(e)
                setEzosApartments([])
            })
            .finally(() => setApartmentFetchDone(true))
    }

    const showSelectionSection = hasSearched && !searchLoading

    const selectedEntrataPropertyList = selectedEntrataIds
        .map((id) => entrataProperties.find((p) => p.id === id))
        .filter((p): p is EntrataProperty => p != null)

    const canSubmit =
        selectedEzosAptId > 0 &&
        selectedEntrataPropertyList.length > 0 &&
        !isSubmitting

    const handleSubmit = async () => {
        if (!canSubmit) return
        try {
            await onboard({
                company_id: selectedEzosAptId,
                entrata_properties: selectedEntrataPropertyList.map((p) => ({
                    entrata_company_id: p.id,
                    name: p.name,
                    address: p.address1,
                    city: p.city,
                    state: p.state ?? undefined,
                    zip: p.zip_code,
                })),
            })
            onSuccess?.()
        } catch {
            // Error already handled in hook
        }
    }

    return (
        <Container
            style={{
                flex: 1,
                padding: theme.spacing(2),
                flexDirection: 'column',
                overflow: 'scroll',
            }}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.h5),
                    fontWeight: 700,
                }}
            >
                Add Multi Entrata Site Apartment
            </span>
            <Container
                style={{ alignItems: 'center', marginTop: theme.spacing(2) }}
            >
                <Selector
                    label="Organization"
                    data={organizationList ?? []}
                    currentValue={selectedOrgId}
                    onChange={(
                        event: React.ChangeEvent<{
                            value: unknown
                        }>,
                    ) => {
                        setSelectedOrgId(event.target.value as number)
                    }}
                    getDisplayString={(org) => org.name}
                    customStyle={{
                        formControl: selectorStyle,
                    }}
                    maxItems={100}
                    searchable
                />
                {/* Fixed-size container: swap button/spinner without changing layout */}
                <div
                    style={{
                        width: 125,
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {searchLoading ? (
                        <CircularProgress size={28} />
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={selectedOrgId === -1}
                            style={{ ...buttonStyle, width: '100%' }}
                            onClick={onSearch}
                        >
                            Search
                        </Button>
                    )}
                </div>
            </Container>

            {/* Selection section: always mounted once first search runs, disabled while loading */}
            {hasSearched && (
                <Container
                    style={{
                        flexDirection: 'column',
                        marginTop: theme.spacing(3),
                    }}
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Selector
                            label="EZOS Apartment (select one)"
                            data={ezosApartments}
                            currentValue={selectedEzosAptId}
                            onChange={(
                                event: React.ChangeEvent<{
                                    value: unknown
                                }>,
                            ) => {
                                setSelectedEzosAptId(
                                    event.target.value as number,
                                )
                            }}
                            getDisplayString={(apt) => apt.name}
                            customStyle={{
                                formControl: {
                                    ...selectorStyle,
                                    minWidth: 280,
                                },
                            }}
                            maxItems={100}
                            searchable
                            noSelectionLabel="Select an EZOS apartment"
                        />
                    </Container>

                    <Container style={{ alignItems: 'center' }}>
                        <Selector
                            label="Entrata Properties (select multiple)"
                            data={entrataProperties}
                            currentValue={selectedEntrataIds}
                            onChange={(
                                event: React.ChangeEvent<{
                                    value: unknown
                                }>,
                            ) => {
                                const value = event.target.value
                                setSelectedEntrataIds(
                                    (Array.isArray(value)
                                        ? value
                                        : [value]) as number[],
                                )
                            }}
                            getDisplayString={(property) => property.name}
                            customStyle={{
                                formControl: {
                                    ...selectorStyle,
                                    minWidth: 280,
                                },
                            }}
                            maxItems={100}
                            searchable
                            multiple
                        />
                    </Container>

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canSubmit}
                        style={{
                            ...buttonStyle,
                            marginTop: theme.spacing(3),
                            alignSelf: 'flex-start',
                        }}
                        onClick={handleSubmit}
                    >
                        {isSubmitting
                            ? 'Submitting...'
                            : 'Onboard Entrata Properties to Apartment'}
                    </Button>
                </Container>
            )}

            <div style={{ flex: 1 }} />
        </Container>
    )
}
